import { memo, useCallback, useMemo, type FC, type PointerEvent } from "react";
import type { PlaneCoordinate, PlanePoint, ToScreenPoint } from "./CartesianPlane";
import DeckBoardsLayer, { type DeckBoardSegment } from "./DeckBoardsLayer";
import {
  getClosingCoordinate,
  getNextCoordinate,
  getTypedNextCoordinate,
  isSegmentNarrowerThanBoardWidth,
  parseTypedDistance,
} from "./deckDrawingGeometry";
import styles from "./DeckDrawingLayers.module.css";
import DeckEdgeMeasurements from "./DeckEdgeMeasurements";
import { getSlopeArrowCoordinates, type SlopeArrow } from "./deckOutline";
import DeckShapeLayer from "./DeckShapeLayer";
import DeckSideHandlesLayer from "./DeckSideHandlesLayer";
import NumberedPointMarkers from "./NumberedPointMarkers";
import { usePlanePoint, type PlanePointStore } from "./planePointStore";
import SlopeArrowLayer from "./SlopeArrowLayer";

type PlanePointFromClient = (clientX: number, clientY: number) => PlanePoint | null;

export type DifferentBoardDragHandler = (
  event: PointerEvent<SVGPolygonElement>,
  segment: DeckBoardSegment,
  toPlanePointFromClient: PlanePointFromClient,
) => void;

type DeckDrawingLayersProps = {
  boardSegments: DeckBoardSegment[];
  boardWidth?: number | null;
  coordinates: PlaneCoordinate[];
  // The shape while one of its sides is being dragged, shown instead of coordinates.
  draftCoordinates: PlaneCoordinate[] | null;
  draggedSideIndex: number | null;
  exportMode: boolean;
  finishCoordinates: (coordinates: PlaneCoordinate[]) => PlaneCoordinate[] | null;
  hasHorizontalBoards?: boolean | null;
  isPanning: boolean;
  lineClassName: string;
  onDifferentBoardDrag: DifferentBoardDragHandler;
  onDifferentBoardDragEnd: DifferentBoardDragHandler;
  onDifferentBoardDragStart: DifferentBoardDragHandler;
  onDimensionChange?: (sideCode: string, value: number) => boolean;
  onSideDrag: (event: PointerEvent<SVGLineElement>, toPlanePointFromClient: PlanePointFromClient) => void;
  onSideDragEnd: (event: PointerEvent<SVGLineElement>) => void;
  onSideDragStart: (event: PointerEvent<SVGLineElement>, sideIndex: number) => void;
  pointLimit: number;
  pointStore: PlanePointStore;
  readOnly: boolean;
  shapeClassName: string;
  slopeArrow: SlopeArrow | null;
  toPlanePointFromClient: PlanePointFromClient;
  toScreenPoint: ToScreenPoint;
  // The next side's length as typed so far (see DeckDrawingPlane), previewed instead of the hover.
  typedDistance: string;
};

// Everything DeckDrawingPlane draws on its plane. The only part following the pointer is the preview
// of the next point: this component subscribes to the hover point itself, and only while there's a
// point left to place - so the plane (and, while the shape is complete, everything here) doesn't
// re-render on pointer moves, and the memoized layers below only when their own props change.
const DeckDrawingLayers: FC<DeckDrawingLayersProps> = ({
  boardSegments,
  boardWidth,
  coordinates,
  draftCoordinates,
  draggedSideIndex,
  exportMode,
  finishCoordinates,
  hasHorizontalBoards,
  isPanning,
  lineClassName,
  onDifferentBoardDrag,
  onDifferentBoardDragEnd,
  onDifferentBoardDragStart,
  onDimensionChange,
  onSideDrag,
  onSideDragEnd,
  onSideDragStart,
  pointLimit,
  pointStore,
  readOnly,
  shapeClassName,
  slopeArrow,
  toPlanePointFromClient,
  toScreenPoint,
  typedDistance,
}) => {
  const visibleCoordinates = draftCoordinates ?? coordinates;
  const point = usePlanePoint(pointStore, !readOnly && !isPanning && visibleCoordinates.length < pointLimit);
  const typedDistanceValue = parseTypedDistance(typedDistance);
  const typedPreviewCoordinate =
    point && typedDistanceValue != null
      ? getTypedNextCoordinate(visibleCoordinates, pointLimit, point, typedDistanceValue)
      : null;
  const nextPreviewCoordinate = point
    ? (typedPreviewCoordinate ?? getNextCoordinate(visibleCoordinates, point, pointLimit))
    : null;
  // Kept by value: the hover point changes far more often than the preview it snaps to.
  const previewX = nextPreviewCoordinate?.x;
  const previewY = nextPreviewCoordinate?.y;
  const previewCoordinate = useMemo(
    () => (previewX != null && previewY != null ? { x: previewX, y: previewY } : null),
    [previewX, previewY],
  );
  const previewPoint =
    previewCoordinate && visibleCoordinates.length > 0
      ? toScreenPoint(previewCoordinate, visibleCoordinates.length)
      : null;
  // Right before auto-close (placing the point that triggers it), preview the whole
  // shape the candidate would produce - including the auto-computed final point - and
  // flag it invalid if it wouldn't actually close into a valid shape (e.g. a side ending
  // up no longer than the parallel one already drawn), instead of only finding out after
  // the point is placed.
  const isPlacingClosingSetupPoint = visibleCoordinates.length === pointLimit - 2;
  const closingPreviewCoordinate = useMemo(
    () =>
      isPlacingClosingSetupPoint && previewCoordinate
        ? getClosingCoordinate([...visibleCoordinates, previewCoordinate], pointLimit)
        : null,
    [isPlacingClosingSetupPoint, pointLimit, previewCoordinate, visibleCoordinates],
  );
  const closingPreviewShape =
    closingPreviewCoordinate && previewCoordinate
      ? [...visibleCoordinates, previewCoordinate, closingPreviewCoordinate]
      : null;
  const uphillArrow = useMemo(
    () =>
      slopeArrow != null && visibleCoordinates.length === pointLimit
        ? getSlopeArrowCoordinates(visibleCoordinates, slopeArrow)
        : null,
    [pointLimit, slopeArrow, visibleCoordinates],
  );
  const previousCoordinate = visibleCoordinates[visibleCoordinates.length - 1];
  const isPreviewSegmentTooNarrow =
    previousCoordinate && previewCoordinate
      ? isSegmentNarrowerThanBoardWidth(previousCoordinate, previewCoordinate, boardWidth, hasHorizontalBoards)
      : false;
  const isPreviewInvalid =
    isPreviewSegmentTooNarrow ||
    (isPlacingClosingSetupPoint && !!previewCoordinate
      ? !closingPreviewShape || !finishCoordinates(closingPreviewShape)
      : false);

  const handleSideDrag = useCallback(
    (event: PointerEvent<SVGLineElement>) => onSideDrag(event, toPlanePointFromClient),
    [onSideDrag, toPlanePointFromClient],
  );
  const handleDifferentBoardPointerDown = useCallback(
    (event: PointerEvent<SVGPolygonElement>, segment: DeckBoardSegment) =>
      onDifferentBoardDragStart(event, segment, toPlanePointFromClient),
    [onDifferentBoardDragStart, toPlanePointFromClient],
  );
  const handleDifferentBoardPointerMove = useCallback(
    (event: PointerEvent<SVGPolygonElement>, segment: DeckBoardSegment) =>
      onDifferentBoardDrag(event, segment, toPlanePointFromClient),
    [onDifferentBoardDrag, toPlanePointFromClient],
  );
  const handleDifferentBoardPointerUp = useCallback(
    (event: PointerEvent<SVGPolygonElement>, segment: DeckBoardSegment) =>
      onDifferentBoardDragEnd(event, segment, toPlanePointFromClient),
    [onDifferentBoardDragEnd, toPlanePointFromClient],
  );

  return (
    <>
      <DeckShapeLayer
        coordinates={visibleCoordinates}
        maxCoordinates={pointLimit}
        closingPreviewCoordinate={closingPreviewCoordinate}
        isPreviewInvalid={isPreviewInvalid}
        outlineClassName={lineClassName}
        previewCoordinate={previewCoordinate}
        shapeClassName={shapeClassName}
        toScreenPoint={toScreenPoint}
      />
      {!readOnly && visibleCoordinates.length === pointLimit && (
        <DeckSideHandlesLayer
          coordinates={visibleCoordinates}
          draggedSideIndex={draggedSideIndex}
          onSideDrag={handleSideDrag}
          onSideDragEnd={onSideDragEnd}
          onSideDragStart={onSideDragStart}
          toScreenPoint={toScreenPoint}
        />
      )}
      {!draftCoordinates && (
        <DeckBoardsLayer
          boardSegments={boardSegments}
          onDifferentBoardPointerDown={readOnly ? undefined : handleDifferentBoardPointerDown}
          onDifferentBoardPointerMove={readOnly ? undefined : handleDifferentBoardPointerMove}
          onDifferentBoardPointerUp={readOnly ? undefined : handleDifferentBoardPointerUp}
          toScreenPoint={toScreenPoint}
        />
      )}
      {uphillArrow && <SlopeArrowLayer start={uphillArrow.start} end={uphillArrow.end} toScreenPoint={toScreenPoint} />}
      <DeckEdgeMeasurements
        coordinates={visibleCoordinates}
        maxCoordinates={pointLimit}
        onCommitSideValue={readOnly ? undefined : onDimensionChange}
        previewCoordinate={previewCoordinate}
        toScreenPoint={toScreenPoint}
        exportMode={exportMode}
      />
      {previewPoint && (
        <div
          className={`${styles.previewPoint} ${isPreviewInvalid ? styles.invalidPreviewPoint : ""}`}
          style={{ left: previewPoint.pixelX, top: previewPoint.pixelY }}
        />
      )}
      <NumberedPointMarkers coordinates={visibleCoordinates} toScreenPoint={toScreenPoint} />
    </>
  );
};

// Memoized: the plane and DeckDrawingPlane also re-render for things these layers don't use (e.g. an
// error message in the toolbar) - the layers then only when their own props change.
export default memo(DeckDrawingLayers);
